import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEntitlement } from "@/lib/entitlement";
import { isEnterpriseActive, isOwner } from "@/lib/featureAccess";
import { scoped } from "@/lib/accountScope";

const LS_KEY = "rsc.enterpriseTeam.v1";

function _loadTeam() {
  try {
    const raw = scoped.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_e) { /* ignore parse errors */ }
  return {
    companyName: "RigSentinel Enterprise",
    seats: 10,
    members: [
      { email: "rigsentinel@gmail.com", role: "admin" },
    ],
  };
}
function _saveTeam(team) {
  try { scoped.setItem(LS_KEY, JSON.stringify(team)); } catch (_e) { /* ignore quota errors */ }
}

/**
 * RC-1 Freemium Enterprise Dashboard (basic CRUD, localStorage-backed).
 * Accessible to Enterprise users + the owner. Free/Pro users are denied.
 */
export default function EnterpriseDashboard() {
  const nav = useNavigate();
  const ent = useEntitlement();
  const [team, setTeam] = useState(_loadTeam);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("member");

  if (ent && !ent.loading && !isEnterpriseActive(ent) && !isOwner(ent)) {
    return (
      <div data-testid="enterprise-locked" className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full rounded-2xl border border-neutral-800 bg-neutral-900 p-8 text-center">
          <div className="text-4xl mb-3">🏢</div>
          <h1 className="text-2xl font-semibold mb-3">Enterprise access required</h1>
          <p className="text-neutral-400 text-sm mb-6">The Enterprise dashboard is available on the Enterprise plan.</p>
          <button
            type="button"
            onClick={() => nav("/pricing")}
            className="w-full rounded-lg bg-amber-500 hover:bg-amber-400 text-neutral-950 font-semibold py-3 transition"
          >
            See Pricing
          </button>
        </div>
      </div>
    );
  }

  const addMember = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (team.members.some((m) => m.email === email)) return;
    if (team.members.length >= team.seats) return;
    const next = { ...team, members: [...team.members, { email, role: newRole }] };
    setTeam(next); _saveTeam(next); setNewEmail("");
  };
  const removeMember = (email) => {
    const next = { ...team, members: team.members.filter((m) => m.email !== email) };
    setTeam(next); _saveTeam(next);
  };
  const toggleRole = (email) => {
    const next = {
      ...team,
      members: team.members.map((m) =>
        m.email === email ? { ...m, role: m.role === "admin" ? "member" : "admin" } : m
      ),
    };
    setTeam(next); _saveTeam(next);
  };

  return (
    <div data-testid="enterprise-dashboard" className="min-h-screen bg-neutral-950 text-neutral-100 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="text-3xl mb-2">🏢 Enterprise Dashboard</div>
          <div className="text-neutral-400">Company: <span className="text-neutral-100 font-medium">{team.companyName}</span></div>
          <div className="text-neutral-400" data-testid="enterprise-seat-count">
            Seats: <span className="text-neutral-100 font-medium">{team.members.length}/{team.seats}</span> used
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-800 bg-neutral-900 p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">👥 Team Members</h2>
          <div className="divide-y divide-neutral-800">
            {team.members.map((m) => (
              <div key={m.email} data-testid={`team-row-${m.email}`} className="flex items-center justify-between py-3">
                <div>
                  <span className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded-full mr-3 ${m.role === "admin" ? "bg-amber-500 text-neutral-950" : "bg-neutral-800 text-neutral-300"}`}>
                    {m.role}
                  </span>
                  <span className="font-medium">{m.email}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid={`team-toggle-${m.email}`}
                    onClick={() => toggleRole(m.email)}
                    className="text-xs rounded-md border border-neutral-700 bg-neutral-800 hover:bg-neutral-700 px-3 py-1.5 transition"
                  >
                    Make {m.role === "admin" ? "Member" : "Admin"}
                  </button>
                  <button
                    type="button"
                    data-testid={`team-remove-${m.email}`}
                    onClick={() => removeMember(m.email)}
                    className="text-xs rounded-md border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-300 px-3 py-1.5 transition"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            {team.members.length === 0 && (
              <div className="py-6 text-center text-neutral-500 text-sm">No team members yet.</div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-neutral-800 bg-neutral-900 p-6">
          <h2 className="text-lg font-semibold mb-4">➕ Add Team Member</h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="teammate@company.com"
              data-testid="team-add-email"
              className="flex-1 rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm"
            />
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              data-testid="team-add-role"
              className="rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm"
            >
              <option value="member">Add as Member</option>
              <option value="admin">Add as Admin</option>
            </select>
            <button
              type="button"
              data-testid="team-add-btn"
              onClick={addMember}
              disabled={team.members.length >= team.seats}
              className="rounded-md bg-amber-500 hover:bg-amber-400 disabled:opacity-60 disabled:cursor-not-allowed text-neutral-950 font-semibold px-4 py-2 text-sm transition"
            >
              Add User
            </button>
          </div>
          {team.members.length >= team.seats && (
            <p className="text-xs text-red-400 mt-2">All {team.seats} seats are in use. Remove a member to add a new one.</p>
          )}
        </div>
      </div>
    </div>
  );
}
